import React, { useEffect, useState } from 'react';
import { Stepper, Step, StepLabel, Button, Typography, Box } from '@mui/material';
import MainCard from 'components/MainCard';
import CompanyInfo from './components/CompanyInfo';
import Playbook from './components/Playbook';
import Theme from './components/Theme';
import ConfirmationDialog from 'components/@extended/ConfirmationDialog';
import useUnsavedChangesWarning from 'hooks/useUnsavedChangesWarning';
import usePrompt from 'hooks/usePrompt';
import { apiRequest } from 'helpers/apiRequests';

const AddGroupCompany = () => {
  const [isDirty, setIsDirty] = useUnsavedChangesWarning();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [themeInfo, setThemeInfo] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  // all filled steps number
  const [filedFormNo, setFiledFormNo] = useState(0);
  const isAllowedToCreateDemoCompany = process.env.REACT_APP_ALLOW_DEMO_COMPANY === 'true';
  const [isOpenModal, setOpenModal] = useState(false);
  const [groupCompanies, setGroupCompanies] = useState([]);
  const steps = ['Information', 'Playbook', 'Theme'];

  const handleNext = () => {
    setFiledFormNo(activeStep + 1);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = (status) => {
    if (status) {
      window.location.reload();
    } else {
      setOpenModal(false);
    }
  };

  const handleJumpToStep = (index) => {
    if (filedFormNo >= index) {
      setActiveStep(index);
    }
  };

  const isEligibleToClick = (index) => filedFormNo >= index;

  useEffect(() => {
    setIsDirty(true);
  }, []);

  useEffect(() => {
    if (isAllowedToCreateDemoCompany && companyInfo?.is_demo_company) {
      fetchDemoTemplateData();
    }
  }, [companyInfo]);

  useEffect(() => {
    getGroupCompanies();
  }, []);

  const getGroupCompanies = async () => {
    try {
      const response = await apiRequest.getGroupCompanies();
      const data = response?.data?.data;
      setGroupCompanies(data);
    } catch (error) {
      console.error('Error fetching group companies:', error);
    }
  };

  const fetchDemoTemplateData = async () => {
    try {
      const response = await apiRequest.getDemoTemplateData(companyInfo?.tenant_scale);
      const data = response?.data?.data;
      if (data) {
        setPlaybook({
          ...data.playbook,
          tenant_playbook_values: data?.playbook?.playbook_values?.map((item, index) => {
            return { ...item, id: index + 1 };
          })
        });
      }
    } catch (error) {
      console.error('Error fetching demo template data:', error);
    }
  };

  usePrompt(isDirty);

  return (
    <MainCard
      content={false}
      sx={{ maxWidth: '728px', margin: 'auto', borderRadius: '16px', padding: '24px 0px' }}
    >
      <Box>
        <Box sx={{ mb: '24px' }}>
          <Typography
            textAlign={'center'}
            variant="h3"
            fontWeight={500}
          >
            Add Company
          </Typography>
        </Box>
        {activeStep != 4 && companyInfo?.is_demo_company && (
          <Box sx={{ mb: '24px', mr: '24px', display: 'flex', justifyContent: 'end' }}>
            <Typography
              color={'error'}
              variant="subtitle2"
            >
              Default demo data is applied. Branches, Departments, and Playbook are preconfigured and cannot be edited.
            </Typography>
          </Box>
        )}
        {/* <form onSubmit={handleSubmit(onSubmit)}> */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            '.MuiStepConnector-line': {
              borderTopStyle: { xs: 'none', sm: 'dashed' }
            },
            '.MuiStepConnector-root': {
              top: '24px',
              left: 'calc(-50% + 35px)',
              right: 'calc(50% + 35px)'
            }
          }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  handleJumpToStep(index);
                }}
                sx={{
                  fontSize: '10px !important',
                  '.MuiStepLabel-label': {
                    fontSize: { xs: '10px !important' }
                  },
                  '.MuiStepLabel-iconContainer': {
                    cursor: isEligibleToClick(index) ? 'pointer' : 'auto'
                  },
                  svg: {
                    height: { xs: '25px', sm: '46px' },
                    width: { xs: '25px', sm: '46px' },
                    '.MuiStepIcon-text': {
                      fontSize: '10px'
                    }
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length ? (
          <div>
            <Typography
              variant="h6"
              gutterBottom
            >
              Confirmation
            </Typography>
            <Button
              variant="contained"
              color="primary"
              type="submit"
            >
              Submit
            </Button>
          </div>
        ) : (
          <Box sx={{ padding: { xs: '20px', sm: '24px 48px' } }}>
            {activeStep === 0 && (
              // Step 1: Information
              <CompanyInfo
                handleNext={handleNext}
                handleBack={handleBack}
                companyInfo={companyInfo}
                setCompanyInfo={setCompanyInfo}
                groupCompanies={groupCompanies}
              />
            )}

            {activeStep === 1 && (
              // Step 4: Playbook
              <Playbook
                handleNext={handleNext}
                handleBack={handleBack}
                playbook={playbook}
                setPlaybook={setPlaybook}
                isDemoCompany={companyInfo?.is_demo_company}
              />
            )}
            {activeStep === 2 && (
              <Theme
                handleBack={handleBack}
                themeInfo={themeInfo}
                setThemeInfo={setThemeInfo}
                companyInfo={companyInfo}
                playbook={playbook}
                setIsDirty={setIsDirty}
              />
            )}
          </Box>
        )}
      </Box>
      <ConfirmationDialog
        isOpenModal={isOpenModal}
        setOpenModal={setOpenModal}
        handleClose={handleClose}
        data={{
          title: 'Reload this page?',
          description: `You are about to reload your page. Are you sure?`,
          buttonTitle: 'Reload'
        }}
      />
    </MainCard>
  );
};

export default AddGroupCompany;
